import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Combobox,
  EInputTypes,
  ETypeOfNotification,
  InputColumnTableProps,
  InputDate,
  InputSwitch,
  InputTable,
  InputTextCustom,
  Modal,
  setNotification,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { useDispatch } from 'react-redux'
import { defaultComboDataDTO } from '../../dto/ComboDataDTO'
import useResizeListener from '../../hooks/useResizeListener'
import {
  defaultServiceBossDTO,
  defaultServiceDetailDTO,
  ServiceBossDTO,
  ServiceDetailDTO,
} from '../../dto/services/ServicesDTO'
import {
  defaultServiceBossDTO as emptyServiceBoss,
  useServicesService,
} from '../../servicesHooks/Services/useServicesService'
import LocationInlineEditActions from '../Location/components/LocationInlineEditActions'
import LocationMobileActionMenu, { LocationMobileActionItem } from '../Location/tabs/components/LocationMobileActionMenu'
import './Services.scss'

interface ServiceFormViewProps {
  serviceId: number
  focusBosses?: boolean
  setServiceId: (serviceId: number | null) => void
  onSaved?: () => void
}

interface ServiceFormErrors {
  externalCode?: string
  serviceName?: string
  bossProfessional?: string
  bossDateBegin?: string
  bossDateEnd?: string
  bossDraft?: string
}

type BossDraftKey = number | 'new' | null

const EMPTY_BOSS_ID = 'new'

function normalizeDateValue(value: Date | null | undefined) {
  return value instanceof Date && !Number.isNaN(value.getTime()) ? value : null
}

function normalizeDateOnly(value: Date | null | undefined) {
  const normalized = normalizeDateValue(value)
  if (!normalized) return null
  return new Date(normalized.getFullYear(), normalized.getMonth(), normalized.getDate())
}

function compareDateValues(a: Date | null | undefined, b: Date | null | undefined) {
  const aTime = normalizeDateOnly(a)?.getTime()
  const bTime = normalizeDateOnly(b)?.getTime()

  if (aTime == null && bTime == null) return 0
  if (aTime == null) return -1
  if (bTime == null) return 1
  return aTime - bTime
}

function getToday() {
  return normalizeDateOnly(new Date())
}

function getNextDay(value: Date | null | undefined) {
  const normalized = normalizeDateOnly(value)
  if (!normalized) return null
  const nextDay = new Date(normalized)
  nextDay.setDate(nextDay.getDate() + 1)
  return nextDay
}

function formatDate(value: Date | null | undefined) {
  const normalized = normalizeDateOnly(value)
  return normalized ? normalized.toLocaleDateString('es-AR') : '-'
}

function isCurrentBoss(boss: ServiceBossDTO, today = getToday()) {
  const dateBegin = normalizeDateOnly(boss.dateBegin)
  const dateEnd = normalizeDateOnly(boss.dateEnd)

  if (!dateBegin || !today) return false
  return compareDateValues(dateBegin, today) <= 0 && (dateEnd == null || compareDateValues(dateEnd, today) > 0)
}

function isEndedBoss(boss: ServiceBossDTO, today = getToday()) {
  const dateEnd = normalizeDateOnly(boss.dateEnd)
  return dateEnd != null && today != null && compareDateValues(dateEnd, today) < 0
}

function sortBosses(items: ServiceBossDTO[]) {
  const sortByBeginAsc = (left: ServiceBossDTO, right: ServiceBossDTO) => compareDateValues(left.dateBegin, right.dateBegin)
  const currentBosses = items.filter((item) => isCurrentBoss(item)).sort(sortByBeginAsc)
  const remainingBosses = items.filter((item) => !isCurrentBoss(item)).sort(sortByBeginAsc)
  return [...currentBosses, ...remainingBosses]
}

function reconcileBossesTimeline(items: ServiceBossDTO[]) {
  return sortBosses(items.map((item) => ({
    ...item,
    dateBegin: normalizeDateOnly(item.dateBegin),
    dateEnd: normalizeDateOnly(item.dateEnd),
  })))
}

function getBossCellClassName(boss: ServiceBossDTO) {
  return isCurrentBoss(boss)
    ? 'services-bosses-cell services-bosses-cell--current'
    : 'services-bosses-cell'
}

function formatBossConflictMessage(conflictDate: Date | null | undefined) {
  return `La fecha de fin debe ser igual o anterior a ${formatDate(conflictDate)} para evitar dias superpuestos.`
}

export default function ServicesFormView({
  serviceId,
  focusBosses = false,
  setServiceId,
  onSaved,
}: Readonly<ServiceFormViewProps>) {
  const dispatch = useDispatch()
  const { buildServiceSavePayload, getServiceById, getServiceProfessionals, saveService } = useServicesService()
  const isMobile = useResizeListener(768)
  const [form, setForm] = useState<ServiceDetailDTO>(defaultServiceDetailDTO)
  const [loading, setLoading] = useState(serviceId > 0)
  const [saving, setSaving] = useState(false)
  const [bossDraft, setBossDraft] = useState<ServiceBossDTO>(defaultServiceBossDTO)
  const [bossEditKey, setBossEditKey] = useState<BossDraftKey>(null)
  const [bossIdsToDelete, setBossIdsToDelete] = useState<number[]>([])
  const [errors, setErrors] = useState<ServiceFormErrors>({})
  const [bossMobileModalVisible, setBossMobileModalVisible] = useState(false)
  const bossesSectionRef = useRef<HTMLDivElement | null>(null)

  const title = serviceId > 0 ? 'Editar servicio' : 'Nuevo servicio'
  const isEditingBoss = bossEditKey !== null

  useEffect(() => {
    let cancelled = false

    async function loadDetail() {
      if (serviceId <= 0) {
        setForm(defaultServiceDetailDTO)
        setBossDraft(defaultServiceBossDTO)
        setBossEditKey(null)
        setBossIdsToDelete([])
        setErrors({})
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const detail = await getServiceById(serviceId)
        if (!cancelled) {
          setForm({
            ...detail,
            bosses: reconcileBossesTimeline(detail.bosses ?? []),
          })
          setBossDraft(defaultServiceBossDTO)
          setBossEditKey(null)
          setBossIdsToDelete([])
          setErrors({})
        }
      } catch (error) {
        console.error(error)
        if (!cancelled) {
          dispatch(setNotification({
            title: 'Error',
            desc: 'No se pudieron cargar los datos del servicio.',
            typeOfNotification: ETypeOfNotification.ERROR,
            icon: 'pi pi-times',
          }))
          setServiceId(null)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadDetail()
    return () => {
      cancelled = true
    }
  }, [dispatch, serviceId, setServiceId])

  useEffect(() => {
    if (!focusBosses || loading) return
    const timer = window.setTimeout(() => {
      bossesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
    return () => window.clearTimeout(timer)
  }, [focusBosses, loading])

  function updateField<K extends keyof ServiceDetailDTO>(field: K, value: ServiceDetailDTO[K]) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  function clearBossErrors() {
    setErrors((current) => ({
      ...current,
      bossProfessional: undefined,
      bossDateBegin: undefined,
      bossDateEnd: undefined,
      bossDraft: undefined,
    }))
  }

  function resetBossDraft() {
    setBossDraft(emptyServiceBoss)
    setBossEditKey(null)
    clearBossErrors()
  }

  function validateBossDraft() {
    const nextErrors: ServiceFormErrors = {}
    const draftBegin = normalizeDateOnly(bossDraft.dateBegin)
    const draftEnd = normalizeDateOnly(bossDraft.dateEnd)

    if (!bossDraft.professional?.id || bossDraft.professional.id <= 0) {
      nextErrors.bossProfessional = 'El profesional es obligatorio.'
    }

    if (!draftBegin) {
      nextErrors.bossDateBegin = 'La fecha de inicio es obligatoria.'
    } else {
      const hasRepeatedBeginDate = form.bosses.some((item, index) => {
        if (bossEditKey !== EMPTY_BOSS_ID && bossEditKey !== null && index === bossEditKey) {
          return false
        }

        return compareDateValues(item.dateBegin, draftBegin) === 0
      })

      if (hasRepeatedBeginDate) {
        nextErrors.bossDateBegin = 'No puede haber dos jefes de servicio con la misma fecha de inicio.'
      }
    }

    if (draftBegin && draftEnd && compareDateValues(draftEnd, draftBegin) <= 0) {
      nextErrors.bossDateEnd = 'La fecha de fin debe ser posterior a la fecha de inicio.'
    }

    return nextErrors
  }

  function buildBossesWithDraftApplied(sourceBosses: ServiceBossDTO[]) {
    const validationErrors = validateBossDraft()
    if (Object.keys(validationErrors).length > 0) {
      setErrors((current) => ({ ...current, ...validationErrors }))
      return null
    }

    const nextBoss: ServiceBossDTO = {
      serviceBossDateId: bossDraft.serviceBossDateId ?? null,
      professional: bossDraft.professional ?? null,
      professionalName: bossDraft.professional?.name ?? '',
      dateBegin: normalizeDateOnly(bossDraft.dateBegin),
      dateEnd: normalizeDateOnly(bossDraft.dateEnd),
    }

    const candidateBosses = bossEditKey === EMPTY_BOSS_ID || bossEditKey === null
      ? [...sourceBosses, nextBoss]
      : sourceBosses.map((item, index) => (index === bossEditKey ? nextBoss : item))

    const ascendingBosses = candidateBosses
      .map((item) => ({
        ...item,
        dateBegin: normalizeDateOnly(item.dateBegin),
        dateEnd: normalizeDateOnly(item.dateEnd),
        isDraft: item === nextBoss,
      }))
      .sort((left, right) => compareDateValues(left.dateBegin, right.dateBegin))

    const draftIndex = ascendingBosses.findIndex((item) => item.isDraft)
    if (draftIndex < 0) {
      return reconcileBossesTimeline(candidateBosses)
    }

    const previousBoss = draftIndex > 0 ? ascendingBosses[draftIndex - 1] : null
    const draftBoss = ascendingBosses[draftIndex]
    const nextBossByDate = draftIndex < ascendingBosses.length - 1 ? ascendingBosses[draftIndex + 1] : null

    if (previousBoss && (!previousBoss.dateEnd || compareDateValues(previousBoss.dateEnd, draftBoss.dateBegin) > 0)) {
      previousBoss.dateEnd = normalizeDateOnly(draftBoss.dateBegin)
    }

    if (nextBossByDate) {
      if (!draftBoss.dateEnd) {
        setErrors((current) => ({
          ...current,
          bossDateEnd: formatBossConflictMessage(nextBossByDate.dateBegin),
        }))
        return null
      }

      if (compareDateValues(draftBoss.dateEnd, nextBossByDate.dateBegin) > 0) {
        setErrors((current) => ({
          ...current,
          bossDateEnd: formatBossConflictMessage(nextBossByDate.dateBegin),
        }))
        return null
      }
    }

    for (let index = 0; index < ascendingBosses.length; index += 1) {
      const currentBoss = ascendingBosses[index]
      const nextBossInTimeline = ascendingBosses[index + 1]

      if (currentBoss.dateBegin && currentBoss.dateEnd && compareDateValues(currentBoss.dateEnd, currentBoss.dateBegin) < 0) {
        setErrors((current) => ({
          ...current,
          bossDraft: 'Revisa las fechas de los jefes de servicio para evitar dias superpuestos.',
        }))
        return null
      }

      if (!nextBossInTimeline) continue

      if (!currentBoss.dateEnd || compareDateValues(currentBoss.dateEnd, nextBossInTimeline.dateBegin) > 0) {
        setErrors((current) => ({
          ...current,
          bossDraft: 'Revisa las fechas de los jefes de servicio para evitar dias superpuestos.',
        }))
        return null
      }
    }

    return sortBosses(
      ascendingBosses.map(({ isDraft: _isDraft, ...item }) => ({
        ...item,
        dateBegin: normalizeDateOnly(item.dateBegin),
        dateEnd: normalizeDateOnly(item.dateEnd),
      })),
    )
  }

  function upsertBossDraft() {
    const nextBosses = buildBossesWithDraftApplied(form.bosses)
    if (nextBosses == null) {
      return false
    }

    setForm((current) => ({
      ...current,
      bosses: nextBosses,
    }))

    resetBossDraft()
    return true
  }

  function hasPartialBossDraft() {
    return Boolean(
      (bossDraft.professional?.id != null && bossDraft.professional.id > 0)
      || normalizeDateOnly(bossDraft.dateBegin)
      || normalizeDateOnly(bossDraft.dateEnd),
    )
  }

  function startBossEdit(boss: ServiceBossDTO, index: number) {
    setBossDraft({
      serviceBossDateId: boss.serviceBossDateId ?? null,
      professional: boss.professional ?? defaultComboDataDTO,
      professionalName: boss.professional?.name ?? boss.professionalName ?? '',
      dateBegin: normalizeDateOnly(boss.dateBegin),
      dateEnd: normalizeDateOnly(boss.dateEnd),
    })
    setBossEditKey(index)
    clearBossErrors()
  }

  function removeBoss(boss: ServiceBossDTO, index: number) {
    setForm((current) => ({
      ...current,
      bosses: reconcileBossesTimeline(current.bosses.filter((_, currentIndex) => currentIndex !== index)),
    }))

    if (boss.serviceBossDateId != null && boss.serviceBossDateId > 0) {
      setBossIdsToDelete((current) => [...current, boss.serviceBossDateId as number])
    }

    if (bossEditKey === index) {
      resetBossDraft()
    }
  }

  function formatBossCardValue(value: string | null | undefined, emptyText = '-') {
    const normalized = String(value ?? '').trim()
    return normalized !== '' ? normalized : emptyText
  }

  function openAddBossMobile() {
    setBossDraft(defaultServiceBossDTO)
    setBossEditKey(EMPTY_BOSS_ID)
    clearBossErrors()
    setBossMobileModalVisible(true)
  }

  function openEditBossMobile(boss: ServiceBossDTO, index: number) {
    startBossEdit(boss, index)
    setBossMobileModalVisible(true)
  }

  function closeBossMobileModal() {
    setBossMobileModalVisible(false)
    resetBossDraft()
  }

  function saveBossFromMobileModal() {
    const saved = upsertBossDraft()
    if (saved) {
      setBossMobileModalVisible(false)
    }
  }

  async function handleSave() {
    const nextErrors: ServiceFormErrors = {}
    if (form.externalCode.trim() === '') {
      nextErrors.externalCode = 'El codigo externo es obligatorio.'
    }
    if (form.serviceName.trim() === '') {
      nextErrors.serviceName = 'El nombre del servicio es obligatorio.'
    }

    if (isEditingBoss || hasPartialBossDraft()) {
      nextErrors.bossDraft = 'Primero debes agregar, guardar o cancelar el jefe de servicio antes de guardar los cambios del servicio.'
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors((current) => ({ ...current, ...nextErrors }))
      return
    }

    setSaving(true)
    try {
      await saveService(buildServiceSavePayload(form, bossIdsToDelete))
      dispatch(setNotification({
        title: serviceId > 0 ? 'Servicio actualizado' : 'Servicio creado',
        desc: serviceId > 0
          ? 'Los cambios se guardaron correctamente.'
          : 'El servicio se creo correctamente.',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))
      setForm(form)
      onSaved?.()
      setServiceId(null)
    } catch (error: unknown) {
      const response = (error as any)?.response
      const status = typeof response?.status === 'number' ? response.status : 0
      const backendMessage = typeof response?.data === 'string' ? response.data.trim() : ''
      const description = status === 409
        ? 'No se pudo guardar el servicio por un conflicto de datos o superposicion de jefaturas.'
        : status === 400 && backendMessage !== ''
          ? backendMessage
          : 'No se pudo guardar el servicio.'

      dispatch(setNotification({
        title: 'Error',
        desc: description,
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
    } finally {
      setSaving(false)
    }
  }

  const bossColumns = useMemo<InputColumnTableProps<ServiceBossDTO>[]>(() => ([
    {
      header: 'Profesional',
      className: 'services-bosses-col services-bosses-col--professional',
      body: (item) => (
        <div className={getBossCellClassName(item)}>
          {item.professional?.name ?? item.professionalName ?? ''}
        </div>
      ),
      input: (
        <div className='services-bosses-combo-wrap'>
          <Combobox
            className='w-full'
            optionLabel='name'
            getItems={getServiceProfessionals}
            value={bossDraft.professional}
            setValue={(value) => {
              setBossDraft((current) => ({
                ...current,
                professional: value ?? defaultComboDataDTO,
                professionalName: value?.name ?? '',
              }))
              clearBossErrors()
            }}
            error={Boolean(errors.bossProfessional)}
            caption={errors.bossProfessional}
          />
        </div>
      ),
    },
    {
      header: 'Fecha de inicio',
      className: 'services-bosses-col services-bosses-col--date',
      body: (item) => (
        <div className={getBossCellClassName(item)}>
          {formatDate(item.dateBegin)}
        </div>
      ),
      input: (
        <InputDate
          className='w-full'
          value={normalizeDateOnly(bossDraft.dateBegin)}
          onChange={(event) => {
            setBossDraft((current) => {
              const nextDateBegin = normalizeDateOnly(event.value)
              const currentDateEnd = normalizeDateOnly(current.dateEnd)
              return {
                ...current,
                dateBegin: nextDateBegin,
                dateEnd: currentDateEnd && nextDateBegin && compareDateValues(currentDateEnd, nextDateBegin) <= 0
                  ? null
                  : currentDateEnd,
              }
            })
            clearBossErrors()
          }}
          error={Boolean(errors.bossDateBegin)}
          caption={errors.bossDateBegin ?? ''}
          style={{ marginBottom: '6px' }}
        />
      ),
    },
    {
      header: 'Fecha de fin',
      className: 'services-bosses-col services-bosses-col--date',
      body: (item) => (
        <div className={getBossCellClassName(item)}>
          {formatDate(item.dateEnd)}
        </div>
      ),
      input: (
        <InputDate
          className='w-full'
          value={normalizeDateOnly(bossDraft.dateEnd)}
          minDate={getNextDay(bossDraft.dateBegin) ?? undefined}
          onChange={(event) => {
            setBossDraft((current) => ({ ...current, dateEnd: normalizeDateOnly(event.value) }))
            setErrors((current) => ({
              ...current,
              bossDateEnd: undefined,
              bossDraft: undefined,
            }))
          }}
          error={Boolean(errors.bossDateEnd)}
          caption={errors.bossDateEnd ?? ''}
          style={{ marginBottom: '6px' }}
        />
      ),
    },
    {
      header: '',
      className: 'services-bosses-col services-bosses-col--actions',
      input: isEditingBoss ? (
        <LocationInlineEditActions
          visible
          onConfirm={upsertBossDraft}
          onCancel={resetBossDraft}
          confirmTooltip='Confirmar jefe de servicio'
          cancelTooltip='Cancelar edicion'
        />
      ) : (
        <Button
          type='button'
          icon='pi pi-plus-circle text-3xl'
          className='buttonSecondary2 p-button-rounded services-bosses-add-btn'
          onClick={() => {
            setBossEditKey(EMPTY_BOSS_ID)
            void upsertBossDraft()
          }}
          aria-label='Agregar jefe de servicio'
        />
      ),
      body: (item, index) => (
        <div className='services-bosses-actions-body'>
          {!isEndedBoss(item) && (
            <Button
              type='button'
              icon='pi pi-file-edit'
              className='p-button-text services-bosses-action-icon-btn'
              onClick={() => startBossEdit(item, index)}
              aria-label='Editar jefe de servicio'
            />
          )}
          <Button
            type='button'
            icon='pi pi-trash'
            className='p-button-text services-bosses-action-icon-btn'
            onClick={() => removeBoss(item, index)}
            aria-label='Eliminar jefe de servicio'
          />
        </div>
      ),
    },
  ]), [bossDraft.dateBegin, bossDraft.dateEnd, bossDraft.professional, errors.bossDateBegin, errors.bossDateEnd, errors.bossProfessional, isEditingBoss])

  if (loading) {
    return (
      <div className='viewContainer services-view'>
        <div className='services-loading'>Cargando servicio...</div>
      </div>
    )
  }

  return (
    <div className='viewContainer services-view'>
      <div className='services-back-action'>
        <Button
          type='button'
          className='btnCancel'
          label='<< Volver'
          onClick={() => setServiceId(null)}
        />
      </div>

      <div className='services-header'>
        <h1 className='services-title'>{title}</h1>
      </div>
      <div className='services-divider' />

      <div className='services-status-row'>
        <div className='services-switch-field services-switch-field-compact'>
          <div className='services-switch-control'>
            <InputSwitch
              id='servicesActiveSwitchId'
              checked={form.active}
              onChange={(e) => updateField('active', Boolean(e.value))}
            />
            <span className='services-switch-status'>{form.active ? 'Activo' : 'Inactivo'}</span>
          </div>
        </div>
      </div>

      <div className='services-crud-content'>
        <section className='services-section services-section-primary'>
          <div className='services-form-grid services-form-grid-primary'>
            <div className='services-field services-field-external-code'>
              <InputTextCustom
                className='w-full'
                label='Codigo externo *'
                value={form.externalCode}
                onChange={(e) => {
                  updateField('externalCode', e?.target?.value ?? '')
                  setErrors((current) => ({ ...current, externalCode: undefined }))
                }}
                inputType={EInputTypes.TEXT}
                error={Boolean(errors.externalCode)}
                caption={errors.externalCode}
              />
            </div>

            <div className='services-field services-field-service-name'>
              <InputTextCustom
                className='w-full'
                label='Nombre del servicio *'
                value={form.serviceName}
                onChange={(e) => {
                  updateField('serviceName', e?.target?.value ?? '')
                  setErrors((current) => ({ ...current, serviceName: undefined }))
                }}
                inputType={EInputTypes.TEXT}
                error={Boolean(errors.serviceName)}
                caption={errors.serviceName}
              />
            </div>
          </div>
        </section>

        <section className='services-section services-section-secondary' ref={bossesSectionRef}>
          <div className='services-bosses-block-card'>
            <div className='services-block-card-header'>
              <h3 className='services-section-title'>Jefe de servicio</h3>
            </div>

            {errors.bossDraft && <small className='p-error services-bosses-global-error'>{errors.bossDraft}</small>}

            {isMobile ? (
              <>
                <div className='services-bosses-mobile-toolbar'>
                  <Button
                    type='button'
                    className='services-bosses-mobile-add-btn'
                    icon='pi pi-plus-circle'
                    label='Agregar'
                    onClick={openAddBossMobile}
                  />
                </div>

                <div className='services-bosses-mobile-card-list'>
                  {form.bosses.map((boss, index) => {
                    const menuItems: LocationMobileActionItem[] = [
                      {
                        label: 'Editar',
                        icon: 'pi pi-file-edit',
                        disabled: isEndedBoss(boss),
                        action: () => openEditBossMobile(boss, index),
                      },
                      {
                        label: 'Eliminar',
                        icon: 'pi pi-trash',
                        action: () => removeBoss(boss, index),
                      },
                    ]

                    return (
                      <article
                        key={`service-boss-${boss.serviceBossDateId ?? index}`}
                        className='services-bosses-mobile-card'
                      >
                        <div className='services-bosses-mobile-card-header'>
                          <h4 className='services-bosses-mobile-card-title'>
                            {formatBossCardValue(boss.professional?.name ?? boss.professionalName, 'Sin profesional')}
                          </h4>
                          <LocationMobileActionMenu items={menuItems} ariaLabel='Opciones del jefe de servicio' />
                        </div>
                        <div className='services-bosses-mobile-card-row'>
                          <span className='services-bosses-mobile-card-key'>Fecha de inicio</span>
                          <span className='services-bosses-mobile-card-value'>{formatDate(boss.dateBegin)}</span>
                        </div>
                        <div className='services-bosses-mobile-card-row'>
                          <span className='services-bosses-mobile-card-key'>Fecha de fin</span>
                          <span className='services-bosses-mobile-card-value'>{formatDate(boss.dateEnd)}</span>
                        </div>
                      </article>
                    )
                  })}
                  {form.bosses.length === 0 && (
                    <p className='services-bosses-mobile-empty'>No hay jefes de servicio cargados.</p>
                  )}
                </div>
              </>
            ) : (
              <InputTable
                className='services-bosses-inputtable'
                columnGap={0}
                headerDivClassName='font-semibold bg-bluegray-50'
                dividerClassName='my-1 border-divider'
                items={form.bosses}
                columns={bossColumns}
                onAdd={() => {}}
                divBtnClassName='services-bosses-hide-native-control'
                divider
                dividerInFirstItem
              />
            )}
          </div>
        </section>
      </div>

      <Modal
        className='services-bosses-mobile-editor-modal'
        visible={bossMobileModalVisible}
        setVisible={setBossMobileModalVisible}
        width='w-medium'
        header={<h1 className='info-dark text-2xl'>{bossEditKey === EMPTY_BOSS_ID ? 'Agregar jefe de servicio' : 'Editar jefe de servicio'}</h1>}
        footerButtonLeftText='Cancelar'
        onClickLeftBtn={closeBossMobileModal}
        footerButtonRightText={bossEditKey === EMPTY_BOSS_ID ? 'Agregar' : 'Guardar'}
        onClickRightBtn={saveBossFromMobileModal}
        onHideCustom={closeBossMobileModal}
      >
        <div className='services-bosses-mobile-modal-content'>
          <div>
            <label className='block mb-2'>Profesional <span className='text-red-500'>*</span></label>
            <Combobox
              className='w-full'
              optionLabel='name'
              getItems={getServiceProfessionals}
              value={bossDraft.professional}
              setValue={(value) => {
                setBossDraft((current) => ({
                  ...current,
                  professional: value ?? defaultComboDataDTO,
                  professionalName: value?.name ?? '',
                }))
                clearBossErrors()
              }}
              error={Boolean(errors.bossProfessional)}
              caption={errors.bossProfessional}
            />
          </div>

          <div>
            <label className='block mb-2'>Fecha de inicio <span className='text-red-500'>*</span></label>
            <InputDate
              className='w-full'
              value={normalizeDateOnly(bossDraft.dateBegin)}
              onChange={(event) => {
                setBossDraft((current) => {
                  const nextDateBegin = normalizeDateOnly(event.value)
                  const currentDateEnd = normalizeDateOnly(current.dateEnd)
                  return {
                    ...current,
                    dateBegin: nextDateBegin,
                    dateEnd: currentDateEnd && nextDateBegin && compareDateValues(currentDateEnd, nextDateBegin) <= 0
                      ? null
                      : currentDateEnd,
                  }
                })
                clearBossErrors()
              }}
              error={Boolean(errors.bossDateBegin)}
              caption={errors.bossDateBegin ?? ''}
            />
          </div>

          <div>
            <label className='block mb-2'>Fecha de fin</label>
            <InputDate
              className='w-full'
              value={normalizeDateOnly(bossDraft.dateEnd)}
              minDate={getNextDay(bossDraft.dateBegin) ?? undefined}
              onChange={(event) => {
                setBossDraft((current) => ({ ...current, dateEnd: normalizeDateOnly(event.value) }))
                setErrors((current) => ({
                  ...current,
                  bossDateEnd: undefined,
                  bossDraft: undefined,
                }))
              }}
              error={Boolean(errors.bossDateEnd)}
              caption={errors.bossDateEnd ?? ''}
            />
          </div>
        </div>
      </Modal>

      <div className='services-crud-actions'>
        <Button
          className='buttonMain2 services-action-btn'
          label='Cancelar'
          onClick={() => setServiceId(null)}
          disabled={saving}
        />
        <Button
          className='buttonMain3 services-action-btn services-primary-btn'
          label={saving ? 'Guardando...' : serviceId > 0 ? 'Guardar cambios' : 'Crear servicio'}
          onClick={() => { void handleSave() }}
          disabled={saving}
        />
      </div>
    </div>
  )
}
